/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { TasksTableDetailRendererProps } from "./TasksTableDetailRenderer";
import { isNullish } from "utils/isNullish";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { CellValueRendererBase } from "pages/gdt/cell-value-renderers/CellValueRendererBase";
import { CellValue } from "@inscopix/ideas-hyperformula";
import { keyBy } from "lodash";

type RowData = {
  name: string;
  paramValue: CellValue;
  group?: { name: string; size: number };
};

export const TasksTableDetailRendererParamsGrid = ({
  tableId,
  rowId,
}: TasksTableDetailRendererProps) => {
  const table = useDataTableContext((s) =>
    s.tables.find((t) => t.id === tableId),
  );

  const groupsById = keyBy(table?.columnGroups, (group) => group.id);
  const row = table?.rows.find((row) => row.id === rowId);

  const rowData: RowData[] = (table?.columns ?? [])
    .map((col, i, cols) => {
      const group = (() => {
        const groupId = col.group;
        const group = groupId !== null ? groupsById[groupId] : undefined;

        if (group !== undefined) {
          let groupSize = 1;
          // Find how many consecutive group members there are
          for (let idx = i + 1; idx < cols.length; idx++) {
            if (cols[idx].group === groupId) {
              groupSize++;
            } else {
              break;
            }
          }
          return { name: group.name, size: groupSize };
        }
      })();

      return {
        name: col.name,
        paramValue: row?.cells[i].value ?? null,
        group,
      } satisfies RowData;
    })
    .map((col, i, cols) => {
      if (i === 0) {
        return col;
      }
      // only first occurrence of group should have group info
      if (col.group !== undefined) {
        for (let j = i + 1; j < cols.length; j++) {
          if (cols[j].group?.name === col.group.name) {
            delete cols[j].group;
          } else {
            break;
          }
        }
      }

      return col;
    });
  const colDefs = useMemo(() => {
    const colDefs: ColDef<RowData>[] = [
      {
        headerName: "",
        cellDataType: false,
        valueGetter: ({ data }) => data?.group,
        rowSpan: (params) => {
          const groupSize = params.data?.group?.size ?? 0;
          if (groupSize > 0) {
            return groupSize;
          }
          return 1;
        },

        cellStyle: { padding: 0 },
        cellRenderer: (params: ICellRendererParams<RowData>) => {
          if (isNullish(params.value) || params.data === undefined) {
            return null;
          }
          return (
            <div
              css={css`
                height: 100%;
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: white;
                padding: 0px;
                margin: 0px;
              `}
            >
              {params.data.group?.name}
            </div>
          );
        },
        flex: 1,
      },
      {
        headerName: "Parameter",
        valueGetter: ({ data }) => data?.name,
        flex: 1.5,
      },
      {
        headerName: "Value",
        valueGetter: ({ data }) => data?.paramValue,
        cellRenderer: ({ data }: ICellRendererParams<RowData>) => {
          const cellValue = data?.paramValue;
          if (cellValue === undefined) {
            return null;
          }
          return <CellValueRendererBase value={cellValue} />;
        },
        flex: 3,
      },
    ];
    return colDefs;
  }, []);

  return (
    <AgGridReact<RowData>
      css={css`
        padding: 1px;
        display: block;
      `}
      rowData={rowData}
      rowHeight={35}
      columnDefs={colDefs}
      defaultColDef={{
        valueGetter: ({ data }) => data?.paramValue,
        resizable: true,
        suppressHeaderMenuButton: true,
      }}
      suppressRowTransform
    />
  );
};
